// Cook — guided step-through: one huge serif step at a time, prev/next
// pills (thumb-sized), an optional timer chip per step, and "Cooked it ✓"
// at the end, which books the ingredient deductions. Keeps the screen awake
// while active via the Wake Lock API where available.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { ArrowLeft, ArrowRight, Check, TimerReset } from "lucide-react";

import { fetcher, post } from "@/api/client";
import { cn } from "@/lib/utils";
import { playClick } from "@/sounds";
import { ingredientAmount, type RecipeFull } from "@/views/RecipeDetail";

export default function Cook() {
  const [params] = useSearchParams();
  const recipeId = params.get("recipe");
  const navigate = useNavigate();

  const { data: recipe } = useSWR<RecipeFull | null>(
    recipeId ? `/api/recipes/${recipeId}` : null,
    fetcher,
  );
  const [index, setIndex] = useState(0);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState<string | null>(null);

  // Keep the tablet awake while cooking.
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    if ("wakeLock" in navigator) {
      (navigator as Navigator & { wakeLock: { request(type: string): Promise<never> } }).wakeLock
        .request("screen")
        .then((l) => {
          lock = l as { release: () => Promise<void> };
        })
        .catch(() => undefined);
    }
    return () => {
      void lock?.release().catch(() => undefined);
    };
  }, []);

  if (!recipeId) {
    return (
      <section className="flex flex-1 items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-display text-6xl leading-[0.95] font-medium tracking-[-0.02em]">
            <span className="block pb-1 italic text-brand-ink">yes,</span>Chef
          </h1>
          <p className="mt-4 text-sm text-dim">
            Pick a recipe in <Link to="/browse" className="underline">Browse</Link> and press
            “Cook this” — the kitchen takes it from there.
          </p>
        </div>
      </section>
    );
  }
  if (!recipe) {
    return <div className="flex-1 p-6"><div className="epicure-skeleton h-52 rounded-[var(--radius-card)]" /></div>;
  }

  const steps = recipe.steps;
  const step = steps[index];
  const done = index >= steps.length;

  async function finish() {
    if (booking) return;
    setBooking(true);
    try {
      const res = await post<{ deducted: { item: string }[] }>(
        `/api/recipes/${recipe!.id}/cooked`,
        {},
      );
      setBooked(
        res.deducted.length
          ? `Booked: ${res.deducted.map((d) => d.item).join(", ")} out of stock.`
          : "Booked — nothing matched the inventory yet.",
      );
    } catch {
      setBooked("Could not book the ingredients — inventory unchanged.");
    } finally {
      setBooking(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col p-4 md:p-6">
      {/* header row */}
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/browse/${recipe.id}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-line-strong px-3.5 py-1.5 text-xs font-semibold text-dim transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" /> {recipe.name}
        </Link>
        {!done && (
          <span className="font-mono text-xs text-dim">
            step {index + 1} / {steps.length}
          </span>
        )}
      </div>

      {/* the step */}
      <div className="flex flex-1 items-center justify-center py-6">
        {done ? (
          <div className="max-w-lg text-center">
            <h1 className="font-display text-6xl leading-[0.95] font-medium tracking-[-0.02em]">
              <span className="block pb-1 italic text-brand-ink">bon</span>
              appétit
            </h1>
            {booked === null ? (
              <button
                type="button"
                disabled={booking}
                onClick={() => void finish()}
                className="mt-8 cursor-pointer rounded-full bg-brand px-7 py-3.5 text-base font-bold text-primary-foreground transition-[transform,background-color] duration-500 ease-epicure hover:-translate-y-0.5 hover:bg-[var(--brand-hover)] active:scale-[0.98] disabled:opacity-35"
              >
                <Check className="mr-1.5 inline size-4" /> Cooked it — book the ingredients
              </button>
            ) : (
              <>
                <p className="mt-6 text-sm text-dim">{booked}</p>
                <button
                  type="button"
                  onClick={() => navigate("/browse")}
                  className="mt-4 cursor-pointer rounded-full border border-line-strong px-5 py-2.5 text-sm font-semibold text-dim transition-colors hover:text-foreground"
                >
                  back to the gallery
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="max-w-2xl text-center">
            <p className="font-display text-3xl leading-snug font-medium text-balance md:text-4xl">
              {step.action}
            </p>
            {step.minutes != null && <TimerChip key={step.id} minutes={step.minutes} />}
          </div>
        )}
      </div>

      {/* ingredients strip + nav */}
      {!done && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap justify-center gap-1.5">
            {recipe.ingredients.slice(0, 10).map((i) => (
              <span
                key={i.id}
                className="rounded-full bg-panel px-3 py-1 font-mono text-[11px] text-dim"
              >
                {i.item_name} {ingredientAmount(i)}
              </span>
            ))}
          </div>
          <div className="flex justify-center gap-3 pb-2">
            <button
              type="button"
              disabled={index === 0}
              onClick={() => {
                setIndex((i) => Math.max(0, i - 1));
                playClick();
              }}
              className="flex cursor-pointer items-center gap-2 rounded-full border border-line-strong px-6 py-3 text-sm font-semibold text-dim transition-colors hover:text-foreground disabled:opacity-35"
            >
              <ArrowLeft className="size-4" /> back
            </button>
            <button
              type="button"
              onClick={() => {
                setIndex((i) => i + 1);
                playClick();
              }}
              className="flex cursor-pointer items-center gap-2 rounded-full bg-brand px-7 py-3 text-sm font-bold text-primary-foreground transition-[transform,background-color] duration-500 ease-epicure hover:-translate-y-0.5 hover:bg-[var(--brand-hover)] active:scale-[0.98]"
            >
              {index === steps.length - 1 ? "done" : "next"} <ArrowRight className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A tappable countdown for steps that carry a duration. */
function TimerChip({ minutes }: { minutes: number }) {
  const [left, setLeft] = useState<number | null>(null); // seconds; null = not started
  const interval = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (interval.current != null) window.clearInterval(interval.current);
    };
  }, []);

  function start() {
    setLeft(minutes * 60);
    playClick();
    interval.current = window.setInterval(() => {
      setLeft((s) => {
        if (s === null || s <= 1) {
          if (interval.current != null) window.clearInterval(interval.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  const label =
    left === null
      ? `${minutes} min`
      : `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;

  return (
    <button
      type="button"
      onClick={left === null ? start : undefined}
      className={cn(
        "mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 font-mono text-sm transition-colors",
        left === 0
          ? "bg-brand text-primary-foreground"
          : left !== null
            ? "border border-brand-ink text-brand-ink"
            : "border border-line-strong text-dim hover:text-foreground",
      )}
    >
      <TimerReset className="size-4" />
      {left === 0 ? "time!" : label}
    </button>
  );
}
